import { Component, EventEmitter, Input, Output, OnInit } from '@angular/core';
import { cloneDeep } from 'lodash-es';
import { ContactMatrix } from '../../../core/models/contact-matrix.model';
import { UniversalSearchModel } from '../../../core/models/universal-search.model';
import { AuthenticationService } from '../../../core/services/authentication.service';
import { CompanyService } from '../../../results/companies/services/companies.service';
import { ContactsService } from '../../../results/contacts/services/contacts.service';
import { RequestBodyService } from '../../services';
import { CommonUtilService } from '../../services/CommonUtil.service';
import { JOB_LABELS, JOB_LABELS_DEPARTMENTS_ABBREVIATIONS } from '../../services/job_labels.service';
import { SavedSearches } from '../../services/saved-searches.service';
import { UserService } from 'src/app/core/services/user.service';
import { AccountAttributesEnum } from '../../enums';
import { SearchService } from 'src/app/results/search/services/search.service';
import { DataTableColumn } from '../ui-components';

@Component({
    selector: 'app-contact-matrix',
    templateUrl: './contact-matrix.component.html',
    standalone: false
})
export class ContactMatrixComponent implements OnInit {
    @Input() data: ContactMatrix;
    @Input() tabName: 'verified' | 'all';
    @Output() contactClick: EventEmitter<any> = new EventEmitter<any>();
    tableData: { key: string, value: string }[];
    columns: DataTableColumn[] = [
        {
            dataField: 'value',
            caption: 'Level',
            isSticky: true,
            allowSorting: false,
            cellClasses: 'border-end px-1',
            headerCellClasses: 'border-end px-1',
        }
    ];

    constructor(
        private requestBodyService: RequestBodyService,
        private commonUtilService: CommonUtilService,
        private contactsService: ContactsService,
        private auth: AuthenticationService,
        private companyService: CompanyService,
        private savedSearchService: SavedSearches,
        private readonly userService: UserService,
        private searchService: SearchService
    ) { }

    ngOnInit(): void {
        this.buildColumns();
        this.buildTableData();
    }

    returnZero() {
        return 0;
    }

    viewContacts(level, department) {
        const companyNames = [];
        let contactData;

        this.requestBodyService.changeContactBodyData(undefined);
        this.requestBodyService.clearSearchSidebar.next(true);
        contactData = cloneDeep(UniversalSearchModel);
        contactData['location_country_codes'] = [];
        if (this.data.Company && this.data.Company[0]['companyIds']) {
            contactData['company_ids'] = [{ companyIds: this.data.Company[0]['companyIds'] }];
        }
        contactData['sort_by'] = 'contact_score';
        contactData['page'] = 1;
        if (this.data.IsHumanVerified) {
            contactData.tiers = '0';
        } else {
            contactData.tiers = this.auth.getPendingTiers().toString();
        }
        if (this.data['company_names']) {
            contactData['company_names'] = this.data['company_names'];
        }
        if (this.data.Type === 'Contact') {
            contactData['location_country_codes'] = [];
            contactData['contact_lists'] = [this.data.ListId];
        } else {
            if (this.data.ListId) {
                contactData['lists'] = [this.data.ListId];
            }
        }
        if (this.data.Company) {

            if (!this.data.ListId) {
                this.data.Company.forEach(element => {
                    companyNames.push(element.companyName);
                });
                contactData['company_names'] = companyNames;
            }
        } else {
            delete contactData['company_ids'];
        }

        if (level.length > 0) {
            contactData.job_levels[level] = true;
        }

        if (department.length > 0) {
            contactData.job_departments[department] = true;
        }

        contactData['accuracy_tier_contact'] = (this.userService.hasAccess(AccountAttributesEnum.ACCURACYTIERS) && this.tabName === 'all') ? 'MV' : contactData['accuracy_tier_contact'];

        this.contactClick.emit();
        this.contactsService.contactLoadedFlag.next(true);
        if (this.companyService.resultPage.resultData.length > 0) {
            this.companyService.resultPage.IsNavigating = true;
        }
        this.contactsService.resultPage.resultData = [];
        this.contactsService.resultPage.verifiedData = [];
        this.contactsService.resultPage.verifiedDataType = null;
        this.contactsService.resultPage.IsNavigating = false;
        this.savedSearchService.currentContactSearchId = '';
        this.savedSearchService.contactActiveSearch = '';
        this.savedSearchService.welComeLoadedFlag.next(true);
        this.searchService.setViewCompanyContacts(true);
        if (this.companyService.resultPage && this.companyService.resultPage.companySidebarData) {
            // contactData.is_international = this.companyService.resultPage.companySidebarData.is_international;
            contactData.location_country_codes = this.companyService.resultPage.companySidebarData.location_country_codes;
        } else {
            if (this.contactsService.resultPage && this.contactsService.resultPage.contactSidebarData) {
                // if (this.contactsService.resultPage.contactSidebarData.is_international) {
                // contactData.is_international = this.contactsService.resultPage.contactSidebarData.is_international;
                contactData.location_country_codes = this.contactsService.resultPage.contactSidebarData.location_country_codes;
                // }
            }
        }
        // if (type === 'total' || location.href.indexOf('intl') !== -1) {
        //     contactData['is_international'] = null;
        // } else {
        //     contactData['is_international'] = 0;
        // }
        contactData['is_international'] = null; // DAAS-6092
        this.commonUtilService.requestBodyUniversalSubscription(contactData);
        this.commonUtilService.churnZeroEvents('Portal - Company Profile - Contact Intel');
    }

    viewAllContacts() {
        this.viewContacts('', '');
    }
    viewLevelContacts(level) {
        this.viewContacts(level, '');
    }
    viewDepartmentContacts(department) {
        this.viewContacts('', department);
    }

    private buildColumns(): void {
        this.columns = [
            ...this.columns,
            ...Object.keys(JOB_LABELS.departments).map((key: string) => ({
                dataField: key,
                caption: JOB_LABELS_DEPARTMENTS_ABBREVIATIONS[key],
                captionTooltip: JOB_LABELS.departments[key],
                cellTemplate: 'departmentCellTemplate',
                cellClasses: 'text-center px-1',
                headerCellClasses: 'text-center px-1',
                allowSorting: false,
                width: '7%',
            })),
            {
                dataField: 'total',
                caption: 'Total',
                captionTooltip: 'Total',
                cellTemplate: 'totalCellTemplate',
                cellClasses: 'text-center px-1',
                headerCellClasses: 'text-center px-1',
                allowSorting: false,
                width: '7%',
            }
        ];
    }

    private buildTableData(): void {
        this.tableData = Object.keys(JOB_LABELS.levels_plural)
            .map((key: string) => ({
                key,
                value: JOB_LABELS.levels_plural[key],
            }));

        this.tableData.push({ key: 'total', value: 'Total'});
    }
}
